import Pusher from 'pusher-js';
import { API_ROUTES } from 'src/constants/routes';
import { httpClient } from 'src/libs';
import { ResSubscriptionFeaturesType, UserType, RegistrationResponseType } from 'src/types/user';
import authToken from './auth-header';
import { getUser } from 'src/features/user/user.slice';
import { createContext } from 'react';

// Helper function to set auth data
const setAuthData = (data: any) => {
  if (data.access_token && typeof window !== 'undefined') {
    localStorage.setItem('user', JSON.stringify(data));

    // Set auth_token cookie that works with both client and server
    document.cookie = `auth_token=${data.access_token}; path=/; max-age=${7 * 24 * 60 * 60}; ${process.env.NODE_ENV === 'production' ? 'secure; ' : ''}samesite=lax`;
  }
};

// Helper function to clear auth data
const clearAuthData = () => {
  localStorage.removeItem('user');
  // Clear the cookie by setting it to expire in the past
  document.cookie = 'auth_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT;';
};

const getPusherOptions = () => {
  const userData = localStorage.getItem('user');
  if (!userData) return null;

  const user = JSON.parse(userData);
  if (!user?.access_token) return null;

  return {
    cluster: "mt1",
    authEndpoint: `${process.env.REACT_APP_API_V3_URL}/private/pusher/authorize`,
    auth: {
      headers: {
        Authorization: `Bearer ${user.access_token}`,
      },
    },
    userAuthentication: {
      endpoint: `${process.env.REACT_APP_API_V3_URL}/private/pusher/authenticate`,
      transport: 'ajax' as const,
      headers: {
        Authorization: `Bearer ${user.access_token}`,
      },
    },
    channelAuthorization: {
      endpoint: `${process.env.REACT_APP_API_V3_URL}/private/pusher/authorize`,
      transport: 'ajax' as const,
      headers: {
        Authorization: `Bearer ${user.access_token}`,
      },
    },
  };
};

// Initialize Pusher instance that will be updated
let pusherInstance: Pusher | null = null;

export const initializePusher = () => {
  if (pusherInstance) {
    try {
      pusherInstance.disconnect();
      pusherInstance = null;
    } catch (error) {
      console.error('Error disconnecting Pusher:', error);
    }
  }

  try {
    const options = getPusherOptions();
    if (!options) {
      return null;
    }

    // Create new Pusher instance with authentication options
    pusherInstance = new Pusher("60f7af116c9d3aeee0d9", options);

    // Enable logging in development
    Pusher.logToConsole = process.env.NODE_ENV !== 'production';

    const userData = localStorage.getItem('user');
    if (!userData) {
      console.error('No user data found in localStorage');
      return null;
    }

    let user;
    try {
      user = JSON.parse(userData);
      if (!user?.id) {
        console.error('User data does not contain an id');
        return null;
      }
    } catch (error) {
      console.error('Error parsing user data:', error);
      return null;
    }

    // Subscribe to private user channel
    const privateChannel = pusherInstance.subscribe(`private-user.${user.id}`);

    // Handle connection states
    pusherInstance.connection.bind('connected', () => {
      // After connection, try to sign in
      try {
        pusherInstance.signin();
      } catch (error) {
        console.error('Error signing in to Pusher after connection:', error);
      }
    });

    pusherInstance.connection.bind('error', (err) => {
      console.error('Pusher connection error:', err);
    });

    // Handle subscription events
    privateChannel.bind('pusher:subscription_succeeded', () => {
      // Removed console.log for performance optimization
    });

    privateChannel.bind('pusher:subscription_error', (error) => {
      console.error('Pusher subscription error:', error);
    });

    return pusherInstance;
  } catch (error) {
    console.error('Error initializing Pusher:', error);
    pusherInstance = null;
    return null;
  }
};

// Initialize Pusher context with null (will be updated when available)
export const PusherContext = createContext<Pusher>(null);

// Export a function to get the current Pusher instance
export const getPusherInstance = () => pusherInstance;

const register = async (user): Promise<RegistrationResponseType> => {
  return httpClient.post(`${API_ROUTES.registration}`, {
    user: {
      ...user,
    },
  })
    .then((response) => {
      if (response.data.access_token) {
        // save it in the localstorage maybe use a local storage plugin
        localStorage.setItem('user', JSON.stringify(response.data));
      }
      return response.data;
    });
};

const login = async (email: string, password: string): Promise<UserType> => {
  return httpClient
    .post(`${API_ROUTES.login}`, {
      user: {
        email,
        password,
      },
    })
    .then((response) => {
      if (response.data.access_token) {
        localStorage.setItem('user', JSON.stringify(response.data));
        // Ensure we initialize Pusher after the user data is saved
        setTimeout(() => initializePusher(), 0);
      }
      return response.data;
    });
};

const smsLogin = async (phone_token: string): Promise<UserType> => {
  return httpClient
    .post(`${API_ROUTES.login_with_sms}`, { phone_token })
    .then((response) => {
      if (response.data.access_token) {
        localStorage.setItem('user', JSON.stringify(response.data));
        // Reinitialize Pusher with new token
        initializePusher();
      }
      return response.data;
    });
};

const sendSms = async (phone: string, email?: string, forLogin?: boolean) => {
  const payload: any = { phone };
  if (email) {
    payload.email = email;
  }
  if (forLogin) {
    payload.for_login = true;
  }
  return httpClient.post(API_ROUTES.send_verification_code, payload);
};

const validateSms = async (phone: string, code: string, email?: string, forLogin?: boolean) => {
  const payload: any = { phone, code };
  if (email) {
    payload.email = email;
  }
  if (forLogin) {
    payload.for_login = true;
  }
  return httpClient.post(API_ROUTES.verify_sms, payload);
};

const logout = async () => {
  // Disconnect Pusher before removing user data
  if (pusherInstance) {
    try {
      pusherInstance.disconnect();
      pusherInstance = null;
    } catch (error) {
      console.error('Error disconnecting Pusher during logout:', error);
    }
  }
  // remove token from local storage
  localStorage.removeItem('user');
};

const forgotPassword = async (data: { email: string }) => {
  return httpClient.post(`${API_ROUTES.forgot_password}`, {
    user: data
  });
};

const resetPassword = async (data: { reset_password_token: string, password: string, password_confirmation: string }) => {
  return httpClient.put(`${API_ROUTES.reset_password}`, {
    user: data
  });
};

const getUserSubscription = async (): Promise<ResSubscriptionFeaturesType> => {
  return httpClient.get(`${API_ROUTES.subscription_paywall_user}`);
};

const createSubscription = async (data: { checkout_session_id: String }) => {
  return httpClient.post(`${API_ROUTES.create_subscription}`, data);
};

const masquerade = async (userId: string) => {
  try {
    // Before masquerading, store the current admin token if not already stored
    if (!localStorage.getItem('original_admin_token')) {
      const currentToken = localStorage.getItem('user');
      if (currentToken) {
        try {
          const userData = JSON.parse(currentToken);
          if (userData.access_token) {
            localStorage.setItem('original_admin_token', userData.access_token);
          }
        } catch (error) {
          console.error('Error storing admin token in masquerade function:', error);
        }
      }
    }

    const API_URL = `${process.env.REACT_APP_API_V3_URL}/private/users/${userId}/masquerade`;
    const response = await httpClient.post(API_URL);

    if (response.data.access_token) {
      setAuthData(response.data);
      // Reinitialize Pusher with new token
      initializePusher();
      // Dispatch getUser to update the Redux state
      getUser();
    }

    return response.data;
  } catch (error) {
    console.error('Error during masquerade:', error);
    throw error;
  }
};

const stopMasquerade = async () => {
  try {
    const originalUserData = localStorage.getItem('original_user');
    if (!originalUserData) {
      console.error('No original user data found');
      return;
    }
    // Get the original user data
    const originalUser = JSON.parse(originalUserData);
    // Get the original admin token - we should have stored this when starting masquerade
    const originalAdminToken = localStorage.getItem('original_admin_token');

    if (!originalAdminToken) {
      console.error('No original admin token found');
      // Clear the masquerade state anyway
      localStorage.removeItem('original_user');

      // If we can't find the token, we have to fall back to logout
      // Clear the cookie by setting it to expire in the past
      clearAuthData();
      window.location.href = '/login';
      return;
    }

    // Clear the masquerade state
    localStorage.removeItem('original_user');
    localStorage.removeItem('original_admin_token');

    // Create a data object similar to what we'd get from a login response
    const adminData = {
      access_token: originalAdminToken,
      user: {
        id: originalUser.id,
        name: originalUser.name
      }
    };

    // Set the admin token and data
    if (adminData.access_token) {
      setAuthData(adminData);
    }

    // Reinitialize Pusher with the admin token
    const pusherInstance = initializePusher();
    if (pusherInstance) {
      try {
        pusherInstance.signin();
      } catch (error) {
        console.error('Error signing in to Pusher after returning from masquerade:', error);
      }
    }

    // Force a reload of the page to ensure all state is refreshed with the admin user
    window.location.href = '/dashboard';

    return true;
  } catch (error) {
    console.error('Error stopping masquerade:', error);
    // If we can't return to admin, clear auth data and redirect to login
    clearAuthData();
    window.location.href = '/login';

    throw error;
  }
};

const pusherSignin = () => {
  // Ensure we have a fresh Pusher instance with current token
  const instance = initializePusher();
  if (instance) {
    try {
      instance.signin();
    } catch (error) {
      console.error('Error signing in to Pusher:', error);
    }
  } else {
    console.warn('Pusher instance not available for signin');
  }
};

const AuthService = {
  register,
  login,
  logout,
  forgotPassword,
  resetPassword,
  getUserSubscription,
  createSubscription,
  sendSms,
  validateSms,
  smsLogin,
  masquerade,
  pusherSignin,
  stopMasquerade,
};

export default AuthService;
